import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import * as _ from 'lodash';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {useTransfersContext} from '../context/TransfersContext';
import FilterTextField from '../../_common/components/FilterTextField';
import {FilterFromDate, FilterToDate} from '../../_common/components/FilterDateRange';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import * as actions from '../_redux/transfersActions';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {generateExcelFile} from '../../_common/utils/excel';
import {transfersHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersTransfers} from '../../_common/constants/exportedFilesHeaders';
import {getPaymentTypeName} from '../../TransfersByAgent/utils';
import {formattingPrice} from '../../_common/utils/format';
import FiltersBar from '../../_common/components/FiltersBar';

const prepareFilter = (queryParams, values) => {
  const {
    agentId,
    agentName,
    fromDate,
    toDate,
  } = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(agentId)) {
    filter.agentId = agentId;
  } else {
    delete newQueryParams.agentId;
  }
  if (Boolean(agentName)) {
    filter.agentName = agentName;
  } else {
    delete newQueryParams.agentName;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }

  return {...newQueryParams, ...filter};
};

export function TransfersFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const transfersContext = useTransfersContext();
  const transfersProps = useMemo(() => {
    return {
      queryParams: transfersContext.queryParams,
      setQueryParams: transfersContext.setQueryParams,
    };
  }, [transfersContext]);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getTransfersListIDPrefix);

    const newQueryParams = prepareFilter(transfersProps.queryParams, values);
    if (!_.isEqual(newQueryParams, transfersProps.queryParams)) {
      transfersProps.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  let agentId = qs.get('agentId') || '';
  let agentName = qs.get('agentName') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';

  const [loading, setLoading] = useState(false);
  const handleExport = () => {
    setLoading(true);
    dispatch(actions.getTransfersListForExcelFile(transfersProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => generateExcelFile(data, 'Transfers.xlsx', transfersHeader))
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        agentId,
        agent,
        datetime,
        lineIds,
        lines,
        customLines
      } = item;
      const agentFullName = [_.get(agent, 'firstName', ''), _.get(agent, 'lastName', '')].filter(Boolean).join(' ');
      const transfersMonthYear = [
        'משכורת ',
        _.get(item, 'paycheck.date.month', ''),
        '.',
        _.get(item, 'paycheck.date.year', '')
      ].filter(Boolean).join('');
      const linesArray = lines ? lines : [];
      const customLinesArray = customLines ? customLines : [];

      const customersFromLines = linesArray.map(line => {
        return [
          _.get(line, 'receipt.customer._id', ''),
          '-',
          _.get(line, 'receipt.customer.name.first', ''),
          _.get(line, 'receipt.customer.name.last', '')
        ].filter(Boolean).join(' ');
      });
      const customersFromCustomLines = customLinesArray.map(item => item.customer ? item.customer : '');
      const customers = [...customersFromLines, ...customersFromCustomLines].filter(Boolean).join(', ');

      const datesFromLines = linesArray.map(line => {
        const {receipt} = line;
        return receipt?.date ? moment(receipt.date).format('DD-MM-YYYY') : '';
      });
      const datesFromCustomLines = customLinesArray.map(item => item.creationDate ? item.creationDate : '');
      const receiptDates = [...datesFromLines, ...datesFromCustomLines].filter(Boolean).join(', ');

      const paymentTypesFromLines = linesArray.map(line => getPaymentTypeName(line.paymentType));
      const paymentTypesFromCustomLines = customLinesArray.map(item => item.paymentType ? item.paymentType : '');
      const paymentTypes = [...paymentTypesFromLines, ...paymentTypesFromCustomLines].filter(Boolean).join(', ');

      const amountsFromLines = linesArray.map(line => {
        return [
          formattingPrice(line.amount ? parseFloat(line.amount).toFixed(2) : 0, true),
          formattingPrice(line.originalAmount ? parseFloat(line.originalAmount).toFixed(2) : 0, true),
        ].filter(Boolean).join('/');
      });
      const amountsFromCustomLines = customLinesArray.map(customLine => {
        const price = customLine.amount ? customLine.amount.toFixed(2) : 0;
        return formattingPrice(price, true);
      });
      const amounts = [...amountsFromLines, ...amountsFromCustomLines].filter(Boolean).join(', ');

      return {
        [headersTransfers[0]]: index + 1,
        [headersTransfers[1]]: agentId || '',
        [headersTransfers[2]]: agentFullName,
        [headersTransfers[3]]: datetime ? moment(datetime).format('DD.MM.YYYY') : '',
        [headersTransfers[4]]: transfersMonthYear,
        [headersTransfers[5]]: lineIds.filter(Boolean).join(', '),
        [headersTransfers[6]]: customers,
        [headersTransfers[7]]: receiptDates,
        [headersTransfers[8]]: paymentTypes,
        [headersTransfers[9]]: amounts,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';

  return (
    <>
      <Formik
        initialValues={{
          agentId,
          agentName,
          fromDate,
          toDate,
        }}
        onSubmit={(values) => applyFilter(values)}
        enableReinitialize={true}
      >
        {({values, ...formikProps}) => (
          <FiltersBar
            filters={
              <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
                <FilterTextField
                  fieldName={'agentId'}
                  placeholder={intl.formatMessage({id: 'transfers-filter-agentID', defaultMessage: 'Agent ID'})}
                  value={values.agentId}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName={'agentName'}
                  placeholder={intl.formatMessage({id: 'transfers-filter-agentName', defaultMessage: 'Agent name'})}
                  value={values.agentName}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterFromDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterToDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
              </div>
            }
            buttons={
            <ButtonExportToExcel
              loading={loading}
              exportFunc={handleExport}
            />
          }
          />
        )}
      </Formik>
    </>
  );
}
